/*
  Flooring Surgeons cookie consent – see snippets/fs-consent.liquid and migration/consent/SETUP.md.

  Source of truth: Shopify's Customer Privacy API (window.Shopify.customerPrivacy, feature 'consent-tracking-api').
  setTrackingConsent() is what Shopify pixels, the Shop app, the Google & YouTube app (Consent Mode v2) and
  consent-aware apps obey. A small localStorage record keeps the date of the choice so we can re-ask after
  RENEW_MS, and acts as the fallback if the API can't load.

  Rules baked in:
  - No tracker is loaded by this file. Until a choice is made, Shopify treats UK visitors as "not consented".
  - First visit: nothing pre-ticked. Accept all / Reject all equally prominent.
  - Global Privacy Control: marketing (and sale of data) stays off unless the visitor switches it on explicitly.
  - Shown only where the store's privacy settings require consent (Shopify region check), after a short pause
    or the first interaction, whichever comes first. Never auto-shown in the theme editor.
*/
(() => {
  const KEY = 'fs-consent';
  const VERSION = 1; // bump to re-ask everybody (e.g. new category or new vendor type)
  const RENEW_MS = 365 * 24 * 60 * 60 * 1000; // re-ask after 12 months
  const SHOW_DELAY_MS = 1200;
  const CATS = ['analytics', 'marketing', 'preferences'];

  const banner = document.getElementById('FsConsent');
  const dialog = document.getElementById('FsConsentPrefs');
  if (!banner || !dialog) return;

  const gpc = navigator.globalPrivacyControl === true;
  let api = null;
  let lastFocus = null;

  /* ---------- storage (every access guarded: private mode / blocked storage) ---------- */
  const readRecord = () => {
    try {
      const rec = JSON.parse(localStorage.getItem(KEY) || 'null');
      return rec && typeof rec.t === 'number' ? rec : null;
    } catch (e) {
      return null;
    }
  };
  const writeRecord = (c, t = Date.now()) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ v: VERSION, t, analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences }));
    } catch (e) {}
  };
  const expired = (rec) => !rec || rec.v !== VERSION || Date.now() - rec.t > RENEW_MS;

  /* ---------- Shopify Customer Privacy API ---------- */
  const loadApi = () =>
    new Promise((resolve) => {
      const S = window.Shopify;
      if (S && S.customerPrivacy && S.customerPrivacy.setTrackingConsent) return resolve(S.customerPrivacy);
      if (!S || typeof S.loadFeatures !== 'function') return resolve(null);
      let done = false;
      const finish = (v) => { if (!done) { done = true; resolve(v); } };
      setTimeout(() => finish(null), 5000);
      try {
        S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) =>
          finish(!err && S.customerPrivacy ? S.customerPrivacy : null)
        );
      } catch (e) {
        finish(null);
      }
    });

  const current = () => {
    if (api && api.currentVisitorConsent) {
      const c = api.currentVisitorConsent() || {};
      return { analytics: c.analytics, marketing: c.marketing, preferences: c.preferences };
    }
    const rec = readRecord();
    if (!rec) return { analytics: '', marketing: '', preferences: '' };
    const yn = (b) => (b ? 'yes' : 'no');
    return { analytics: yn(rec.analytics), marketing: yn(rec.marketing), preferences: yn(rec.preferences) };
  };

  // Google Consent Mode v2 for any gtag/GTM the theme may add later. The Google & YouTube app already
  // reads the Customer Privacy API itself; this only updates a gtag that is already on the page.
  const updateGoogle = (c) => {
    if (typeof window.gtag !== 'function') return;
    const g = (b) => (b ? 'granted' : 'denied');
    window.gtag('consent', 'update', {
      ad_storage: g(c.marketing),
      ad_user_data: g(c.marketing),
      ad_personalization: g(c.marketing),
      analytics_storage: g(c.analytics),
      functionality_storage: 'granted',
      personalization_storage: g(c.preferences),
      security_storage: 'granted',
    });
  };

  // A choice made before the API has loaded (fast click on a slow connection) is handed to it once it's ready –
  // otherwise only our own record had it, and the next page asked again (owner, 10 Oct 2026).
  let apiReady = null;
  const toApi = (choice, done) => {
    if (api && api.setTrackingConsent) {
      try {
        // UK has no "sale of data" opt-out regime; tie it to marketing so one "no" means no.
        api.setTrackingConsent({ ...choice, sale_of_data: choice.marketing }, done);
        return;
      } catch (e) {}
    }
    done();
  };

  const save = (c) => {
    const choice = { analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences };
    writeRecord(choice);
    updateGoogle(choice);
    const announce = () => document.dispatchEvent(new CustomEvent('fs:consent', { detail: choice }));
    if (api || !apiReady) toApi(choice, announce);
    else apiReady.then(() => toApi(choice, announce));
  };

  /* ---------- UI ---------- */
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const showBanner = () => {
    if (!banner.hidden) return;
    // The stylesheet loads async; never reveal unstyled markup (it sits at the top of <body>).
    const css = document.getElementById('FsConsentCss');
    if (css && css.media !== 'all') {
      css.addEventListener('load', () => setTimeout(showBanner), { once: true });
      return;
    }
    banner.querySelector('[data-fs-consent-gpc]').hidden = !gpc;
    banner.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => banner.classList.add('is-open')));
  };
  const hideBanner = () => {
    if (banner.hidden) return;
    banner.classList.remove('is-open');
    const done = () => { banner.hidden = true; };
    if (reduceMotion()) done();
    else setTimeout(done, 260);
  };

  const fillForm = () => {
    const c = current();
    const firstTime = CATS.every((k) => c[k] === '' || c[k] === undefined);
    CATS.forEach((k) => {
      const box = dialog.querySelector(`[name="${k}"]`);
      // Never pre-ticked on a first visit; otherwise reflect the saved choice.
      box.checked = firstTime ? false : c[k] === 'yes';
    });
  };

  const openPrefs = () => {
    lastFocus = document.activeElement;
    fillForm();
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('fs-consent-lock');
    const first = dialog.querySelector('.fs-consent-switch');
    if (first) first.focus();
  };
  const closePrefs = () => {
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  };
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('fs-consent-lock');
    const target = !banner.hidden ? banner.querySelector('[data-fs-consent="manage"]') : lastFocus;
    if (target && typeof target.focus === 'function' && document.contains(target)) target.focus();
  });
  // Click on the backdrop closes (the choice is not changed).
  dialog.addEventListener('click', (e) => { if (e.target === dialog) closePrefs(); });

  const act = (what) => {
    if (what === 'accept') save({ analytics: true, marketing: !gpc, preferences: true });
    else if (what === 'reject') save({ analytics: false, marketing: false, preferences: false });
    else if (what === 'save') {
      const f = {};
      CATS.forEach((k) => { f[k] = dialog.querySelector(`[name="${k}"]`).checked; });
      save(f);
    } else if (what === 'manage') return openPrefs();
    else if (what === 'close') return closePrefs();
    closePrefs();
    hideBanner();
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-fs-consent]');
    if (btn && (banner.contains(btn) || dialog.contains(btn))) {
      e.preventDefault();
      act(btn.getAttribute('data-fs-consent'));
      return;
    }
    // Also catch Shopify's own reshow link (policy list / apps) so only one consent UI ever appears.
    const opener = e.target.closest('[data-fs-consent-open], a[href$="#shopifyReshowConsentBanner"]');
    if (opener) {
      e.preventDefault();
      openPrefs();
    }
  });

  // Public hook for other scripts / apps.
  window.FSConsent = { open: openPrefs, get: current, showBanner };

  /* ---------- decide whether to ask ---------- */
  const init = async () => {
    apiReady = loadApi();
    api = await apiReady;
    if (window.Shopify && window.Shopify.designMode) return; // theme editor: reachable via footer link only

    const rec = readRecord();
    let ask;
    if (api) {
      const c = current();
      const undeclared = CATS.some((k) => !c[k]);
      let regionNeeds = false;
      try {
        regionNeeds = !!(api.shouldShowBanner() || (api.isRegulationEnforced && api.isRegulationEnforced()));
      } catch (e) {}
      if (!regionNeeds) return; // store settings don't require consent here
      if (undeclared && rec && !expired(rec)) {
        // The visitor already chose (saved here only, e.g. clicked before the API loaded): give it to Shopify, don't ask again.
        toApi({ analytics: !!rec.analytics, marketing: !!rec.marketing, preferences: !!rec.preferences }, () => {});
        updateGoogle(rec);
        ask = false;
      } else if (undeclared) ask = true;
      else if (!rec) {
        // Consent already given elsewhere (checkout, older banner): adopt it, start the renewal clock now.
        writeRecord({ analytics: c.analytics === 'yes', marketing: c.marketing === 'yes', preferences: c.preferences === 'yes' });
        ask = false;
      } else ask = expired(rec);
    } else {
      ask = expired(rec); // API unavailable: fall back to our own record
    }
    if (!ask) return;

    // Appear after a short pause or the first interaction – whichever comes first.
    let timer;
    const go = () => {
      clearTimeout(timer);
      ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((t) => window.removeEventListener(t, go, { passive: true }));
      showBanner();
    };
    timer = setTimeout(go, SHOW_DELAY_MS);
    ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((t) => window.addEventListener(t, go, { passive: true, once: true }));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
